import { reaction } from 'mobx';
import { Router } from 'director';

import { getDashboardBasePath } from '@ratehub/base-ui';

import { TabValues } from '../definitions/TabValues';
import PAGES from '../definitions/Pages';
import { TabValues as TAB_VALUES } from '../definitions/TabValues';


/**
 * @public
 * Start monitoring and updating window.history based on which page we are showing.
 * @param {object} context
 * @param {string} locale
 * @returns {function} disposer
 */
function startHistoryWatcher(context) {
    if (typeof window === 'undefined') {
        return null;
    }

    const basePath = getDashboardBasePath(context.viewStore.locale);

    // Couple window.location with our ViewStore's active page.
    initializeRouter(context, basePath);

    // Start our reaction that updates the URL when our page/tab changes.
    return reaction(
        () => context.viewStore && ({
            activePage: context.viewStore.selectedPageId,
            activeTab: context.viewStore.savedDocuments.selectedTabId,
        }),
        (value) => {
            if (!value?.activePage || !value?.activeTab) {
                return;
            }

            const queryParams = window.location.search;
            const currentPath = `${window.location.pathname}${queryParams}`;
            const updatedPath = `${basePath}${getRouteForPage(value.activePage, value.activeTab)}${queryParams}`;

            if (currentPath !== updatedPath) {
                window.history.pushState(null, '', updatedPath);
            } else {
                window.history.replaceState(null, '', updatedPath);
            }
        },
        {
            name: 'startHistoryWatcher.updateURLOnTabChange',
            fireImmediately: true,
        },
    );
}

/**
 * @private
 * Start watching for URL changes, and update our ViewStore to show the appropriate page.
 * @param {object} context
 * @param {string} basePath
 * @returns {object}
 */
function initializeRouter(context, basePath) {
    const routes = {
        [basePath]: {
            [PAGES.signUpWithLink.route]: {
                on: (magicLink, startTab) => {
                    if (context.isLoggedIn) {
                        context.viewStore.showMyQuotes();
                    } else {
                        context.viewStore.showSignUpWithLink(magicLink, startTab);
                    }
                },
            },
            [PAGES.myQuotes.route]: {
                [PAGES.mortgage.route]: () => {
                    context.viewStore.showMyQuotes(TabValues.MORTGAGE);
                },
                [PAGES.autoInsurance.route]: () => {
                    context.viewStore.showMyQuotes(TabValues.AUTO);
                },
                [PAGES.homeInsurance.route]: () => {
                    context.viewStore.showMyQuotes(TabValues.HOME);
                },
                [PAGES.creditCard.route]: () => {
                    context.viewStore.showMyQuotes(TabValues.CREDIT_CARD);
                },
                on: () => {
                    context.viewStore.showMyQuotes();
                },
            },
            [PAGES.profileDetails.route]: {
                on: () => {
                    context.viewStore.showProfileDetails();
                },
            },
            on: () => {
                context.viewStore.showMyQuotes();
            },
        },
    };

    const router = new Router(routes)
        .configure({
            html5history: true,
        });

    // When this is being run on the server, this function will NOT exist!
    // This was added to handle cases where the window check seems to pass,
    // but the function is still not available ....
    if (typeof router.init === 'function') {
        return router.init();
    }

    return router;
}


/**
 * @private
 * Get the route to use for a page+tab combination.
 * @param {string} pageId
 * @param {string} tabId
 * @returns {string}
 */
function getRouteForPage(pageId, tabId = undefined) {
    // SPECIAL CASE: if on the documents page, each tab has a unique URL
    if (pageId === PAGES.myQuotes.id) {
        switch (tabId) {
            case TAB_VALUES.MORTGAGE:
                return PAGES.mortgage.fullRoute;
            case TAB_VALUES.AUTO:
                return PAGES.autoInsurance.fullRoute;
            case TAB_VALUES.HOME:
                return PAGES.homeInsurance.fullRoute;
            case TAB_VALUES.CREDIT_CARD:
                return PAGES.creditCard.fullRoute;
            default:
                return PAGES.myQuotes.fullRoute;
        }
    }

    return PAGES[pageId]?.fullRoute ?? '';
}

export default startHistoryWatcher;
